<template>
  <article v-if="episode" class="episode-full">
    <div class="episode-full__nav">
      <BackButton to="/radio/audiothek" />
      <BackendLink :path />
    </div>
    <div class="episode-full__inner">
      <h1 class="episode-full__title">
        {{ episode.title }}
      </h1>
      <div class="episode-full__player">
        <LazyEpisodePlayer
          :tags="episode.tags"
          :languages="episode.languages"
          :show="episode.show"
          :start="episode.fieldDate.start"
          :end="episode.fieldDate.end"
          :repeat="episode.repeat"
        />
      </div>
      <div v-if="episode.fieldText" class="episode-full__text" v-html="useSanitizeHtml(episode.fieldText)" />
      <div class="episode-full__links-list">
        <IconLink
          :url="{ path: '/radio/feedback', query: { show: encodeURI(episode.show?.title ? episode.show?.title : ''), episode: encodeURI(`${episode.title} ${episode.fieldDate.start ? ` – ${useFormatDate('fullDateShort', episode.fieldDate.start)}` : `` }`) }}"
          :label="`Feedback Formular`"
          icon="CircleArrowRightFilled"
        />
        <div
          v-for="(link, index) in episode.links"
          :key="`episode-full__link--${link.uri.path}-${index}`"
          class="episode-full__link"
        >
          <IconLink :url="link.uri.path" :label="link.title" />
        </div>
      </div>
      <div class="episode-full__tags-list">
        <div v-for="(tag, index) in episode.tags" :key="index" class="episode-full__tag">
          {{ tag.label }}
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import type { UseSeoMetaInput } from '@unhead/schema'
import { type EpisodeFullFragment, type EpisodeQuery } from '#graphql-operations'

const props = defineProps<{
  path: string
}>()

const { data } = await useAsyncGraphqlQuery('episode', { path: props.path }, {
  transform: function (v) {
    return v.data as EpisodeQuery
  }
})

const route: EntityRoute | undefined | null = data.value?.route

await useHandleRedirect(data.value?.route)

if (route?.entity === undefined) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

const episode: EpisodeFullFragment = route.entity

const description = useStripHTML(episode.fieldText ?? '')

const ogImageUrlEpisode = useImageFallback(episode?.image)
const ogImageUrlShow = useImageFallback(episode?.show?.image)
const ogImageUrl = ogImageUrlEpisode || ogImageUrlShow

const metatags: UseSeoMetaInput = {
  title: episode.title,
  description,
  ogDescription: description
}

if (ogImageUrl.length > 0) {
  metatags.ogImageUrl = ogImageUrl
}

useSeoMeta(metatags)

</script>

<style lang="scss">
@use "~/assets/sass/tools";

.episode-full {
  @include tools.grid;

  &__nav {
    display: flex;
    justify-content: space-between;
    grid-column: 1 / span 6;
  }

  &__back {
    @include tools.button-borderless;
    @include tools.typo-button-small;
    display: flex; // stylelint-disable-line
    align-items: center;
    gap: 15px;
    grid-column: 1 / span 6;

    &:hover {
      color: var(--color-text);
      background-color: var(--color-background);
    }
  }

  &__inner {
    @include tools.page-grid-columns;
    display: flex;
    flex-direction: column;
    gap: var(--grid-gutter);
  }

  &__title {
    @include tools.typo-heading-large;
    margin: 0;
    color: var(--color-text);
    -webkit-text-stroke: 0 currentcolor;
  }

  &__player {
    margin-bottom: var(--space--m);
  }

  &__text {
    p {
      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__links-list {
    display: flex;
    flex-direction: column;
    gap: var(--space--s);
  }

  &__tags-list {
    display: flex;
    gap: var(--space--s);
    flex-wrap: wrap;
  }

  &__tag {
    @include tools.tag-big;
  }
}
</style>
